import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { LinearIssueOption, Repo } from "@rivets/core";
import { client } from "@/lib/api";
import { qk } from "@/lib/query-keys";
import {
  pickableBranches,
  pickablePullRequests,
  taskStartLabel,
  type TaskStartSelection,
  type TaskStartTab,
} from "./taskStart";

const TABS: readonly { id: TaskStartTab; label: string }[] = [
  { id: "pullRequests", label: "Pull requests" },
  { id: "branches", label: "Branches" },
  { id: "issues", label: "Issues" },
];

type Row = {
  key: string;
  selection: TaskStartSelection;
  icon: ReactNode;
  label: string;
  mono?: boolean;
  detail?: string;
  meta?: string;
  disabled?: boolean;
  title?: string;
};

/**
 * What a new task starts from: an open pull request or existing branch (the
 * task works on that branch itself) or a Linear issue (a new branch, with the
 * issue as the agent's context). Nothing picked means a fresh branch off the
 * repository's default branch.
 *
 * Built from `MenuSelect`'s trigger and popover so it sits in the modal header
 * like the repository picker beside it, with a tab row above the search.
 */
export function TaskStartPicker({
  orgId,
  repo,
  value,
  onChange,
}: {
  orgId: string;
  repo: Repo | undefined;
  value: TaskStartSelection | undefined;
  onChange: (value: TaskStartSelection | undefined) => void;
}) {
  const githubApp = repo?.auth === "github-app";
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<TaskStartTab>("pullRequests");
  const [search, setSearch] = useState("");
  const [issueQuery, setIssueQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const cursorFromPointer = useRef(false);

  const branchesQuery = useQuery({
    queryKey: qk.repoBranches(repo?.id ?? ""),
    queryFn: () => client.listRepoBranches(repo!.id),
    enabled: open && tab === "branches" && Boolean(repo),
  });
  const pullsQuery = useQuery({
    queryKey: qk.repoPullRequests(repo?.id ?? ""),
    queryFn: () => client.listRepoPullRequests(repo!.id),
    enabled: open && tab === "pullRequests" && githubApp,
  });
  // Linear is searched server-side: its own index knows descriptions and
  // comments, and an organization's open issues do not fit in one page.
  const issuesQuery = useQuery({
    queryKey: qk.linearIssues(orgId, issueQuery),
    queryFn: () => client.listLinearIssues(orgId, issueQuery),
    enabled: open && tab === "issues",
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (tab !== "issues") return;
    const timer = window.setTimeout(() => setIssueQuery(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search, tab]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setIssueQuery("");
    setTab(value?.kind === "branch" ? "branches" : value?.kind === "linearIssue" ? "issues" : githubApp ? "pullRequests" : "branches");
    searchRef.current?.focus();
  }, [open]);

  useEffect(() => { setActiveIndex(0); }, [search, tab]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown, true);
    return () => document.removeEventListener("mousedown", onPointerDown, true);
  }, [open]);

  // Same capture-phase Escape as `MenuSelect`: close this menu, not the modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      rootRef.current?.querySelector<HTMLButtonElement>(".menu-select-trigger")?.focus();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  const rows: Row[] = useMemo(() => {
    if (tab === "branches") {
      return pickableBranches(branchesQuery.data?.branches ?? [], repo?.defaultBranch, search).map((branch) => ({
        key: `branch:${branch}`,
        selection: { kind: "branch", branch },
        icon: <BranchGlyph />,
        label: branch,
        mono: true,
      }));
    }
    if (tab === "pullRequests") {
      return pickablePullRequests(pullsQuery.data?.pullRequests ?? [], search).map((pull) => ({
        key: `pr:${pull.number}`,
        selection: { kind: "pullRequest", pullRequest: pull },
        icon: <PullRequestGlyph draft={pull.draft} />,
        label: pull.title,
        detail: [pull.headBranch, pull.author].filter(Boolean).join(" · "),
        meta: pull.draft ? `Draft · #${pull.number}` : `#${pull.number}`,
        disabled: pull.fromFork,
        title: pull.fromFork ? "Pull requests from forks can’t be checked out" : undefined,
      }));
    }
    return (issuesQuery.data?.issues ?? []).map((issue) => ({
      key: `issue:${issue.id}`,
      selection: { kind: "linearIssue", issue },
      icon: <IssueStatusGlyph state={issue.state} />,
      label: issue.title,
      detail: [issue.identifier, issue.state?.name, issue.assignee].filter(Boolean).join(" · "),
    }));
  }, [tab, search, branchesQuery.data, pullsQuery.data, issuesQuery.data, repo?.defaultBranch]);

  const status = tab === "branches"
    ? branchesQuery.isPending ? "Loading branches…" : branchesQuery.isError ? "Could not load branches." : rows.length === 0 ? "No other branches" : undefined
    : tab === "pullRequests"
      ? !githubApp ? "Pull requests need the GitHub App."
        : pullsQuery.isPending ? "Loading pull requests…" : pullsQuery.isError ? "Could not load pull requests." : rows.length === 0 ? "No open pull requests" : undefined
      : issuesQuery.isPending ? "Loading issues…"
        : issuesQuery.isError ? "Could not load Linear issues."
          : issuesQuery.data?.connected === false ? "Connect Linear under Settings → Integrations to start from an issue."
            : rows.length === 0 ? "No matching issues" : undefined;

  useEffect(() => {
    if (!open) return;
    if (cursorFromPointer.current) {
      cursorFromPointer.current = false;
      return;
    }
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  function commit(row: Row | undefined) {
    if (!row || row.disabled) return;
    onChange(row.selection);
    setOpen(false);
    rootRef.current?.querySelector<HTMLButtonElement>(".menu-select-trigger")?.focus();
  }

  function move(delta: number) {
    cursorFromPointer.current = false;
    if (rows.length === 0) return;
    let next = activeIndex;
    for (let step = 0; step < rows.length; step += 1) {
      next = (next + delta + rows.length) % rows.length;
      if (!rows[next]?.disabled) break;
    }
    setActiveIndex(next);
  }

  function onPopoverKeyDown(event: ReactKeyboardEvent) {
    if (event.key === "ArrowDown") { event.preventDefault(); move(1); }
    else if (event.key === "ArrowUp") { event.preventDefault(); move(-1); }
    else if (event.key === "Enter") { event.preventDefault(); commit(rows[activeIndex]); }
    else if (event.key === "Tab" && (event.target as HTMLElement) === searchRef.current) {
      // Tab cycles the three sources without leaving the search field.
      event.preventDefault();
      const index = TABS.findIndex((item) => item.id === tab);
      setTab(TABS[(index + (event.shiftKey ? TABS.length - 1 : 1)) % TABS.length]!.id);
    }
  }

  const selectedKey = value
    ? value.kind === "branch" ? `branch:${value.branch}` : value.kind === "pullRequest" ? `pr:${value.pullRequest.number}` : `issue:${value.issue.id}`
    : undefined;
  const triggerLabel = value ? taskStartLabel(value) : repo ? `from ${repo.defaultBranch}` : "Start from";

  return (
    <div className="menu-select task-start-picker" ref={rootRef}>
      <button
        type="button"
        className={`menu-select-trigger menu-select-md${open ? " open" : ""}`}
        style={{ minWidth: 150, maxWidth: 300 }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Start from"
        title={value ? taskStartLabel(value) : "Start from a pull request, branch or Linear issue"}
        disabled={!repo}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="menu-select-leading" aria-hidden="true">
          {value?.kind === "pullRequest" ? <PullRequestGlyph draft={value.pullRequest.draft} />
            : value?.kind === "linearIssue" ? <IssueStatusGlyph state={value.issue.state} />
              : <BranchGlyph />}
        </span>
        <span className={`menu-select-label${value?.kind === "branch" || !value ? " mono" : ""}${value ? "" : " placeholder"}`}>
          {triggerLabel}
        </span>
        <svg className="menu-select-chevron" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1.5 5 5l4-3.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {value ? (
        <button type="button" className="task-start-clear" aria-label="Start from the default branch instead" title="Clear" onClick={() => onChange(undefined)}>
          <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3 3l6 6M9 3 3 9" /></svg>
        </button>
      ) : null}
      {open ? (
        <div className="menu-select-popover menu-select-popover-start task-start-popover" role="dialog" aria-label="Start from" onKeyDown={onPopoverKeyDown}>
          <div className="quiet-tabs task-start-tabs" role="tablist" aria-label="Start from">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                data-label={item.label}
                className={`quiet-tab${tab === item.id ? " active" : ""}`}
                onClick={() => { setTab(item.id); searchRef.current?.focus(); }}
              ><span>{item.label}</span></button>
            ))}
          </div>
          <input
            ref={searchRef}
            className="menu-select-search"
            type="text"
            autoComplete="off"
            spellCheck={false}
            aria-label={`Search ${TABS.find((item) => item.id === tab)!.label.toLowerCase()}`}
            placeholder={tab === "pullRequests" ? "Search by title, number or branch…" : tab === "branches" ? "Search branches…" : "Search issues…"}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="menu-select-list" role="listbox" aria-label={TABS.find((item) => item.id === tab)!.label} ref={listRef}>
            {status ? <div className="menu-select-empty">{status}</div> : null}
            {rows.map((row, index) => (
              <button
                key={row.key}
                type="button"
                role="option"
                aria-selected={row.key === selectedKey}
                data-active={index === activeIndex}
                className={`menu-select-option${row.key === selectedKey ? " selected" : ""}`}
                disabled={row.disabled}
                title={row.title}
                onMouseEnter={() => {
                  if (index === activeIndex) return;
                  cursorFromPointer.current = true;
                  setActiveIndex(index);
                }}
                onClick={() => commit(row)}
              >
                <span className="menu-select-leading" aria-hidden="true">{row.icon}</span>
                <span className="menu-select-option-copy">
                  <span className={`menu-select-option-label${row.mono ? " mono" : ""}`}>{row.label}</span>
                  {row.detail ? <span className="menu-select-option-detail">{row.detail}</span> : null}
                </span>
                {row.meta ? <span className="menu-select-option-meta">{row.meta}</span> : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function BranchGlyph() {
  return (
    <svg className="task-start-glyph" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="4.5" cy="3.5" r="1.6" /><circle cx="4.5" cy="12.5" r="1.6" /><circle cx="11.5" cy="5.5" r="1.6" />
      <path d="M4.5 5.1v5.8M11.5 7.1c0 2.4-2.2 2.9-5.6 4.1" />
    </svg>
  );
}

function PullRequestGlyph({ draft }: { draft: boolean }) {
  return (
    <svg className={`task-start-glyph task-start-pr${draft ? " draft" : ""}`} viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="4.5" cy="3.5" r="1.6" /><circle cx="4.5" cy="12.5" r="1.6" /><circle cx="11.5" cy="12.5" r="1.6" />
      <path d="M4.5 5.1v5.8M11.5 10.9V6.5a2 2 0 0 0-2-2H7.5M9 3l-1.5 1.5L9 6" />
    </svg>
  );
}

/** Linear's own status shapes, drawn in the workflow state's colour. */
export function IssueStatusGlyph({ state }: { state: LinearIssueOption["state"] }) {
  const color = state?.color ?? "var(--text-tertiary)";
  const type = state?.type ?? "backlog";
  return (
    <svg className="task-start-glyph task-start-issue" viewBox="0 0 14 14" aria-hidden="true" style={{ color }}>
      {type === "completed" || type === "canceled" ? (
        <>
          <circle cx="7" cy="7" r="6" fill="currentColor" stroke="none" />
          <path
            d={type === "completed" ? "M4.3 7.2 6.2 9 9.8 5.2" : "M4.9 4.9l4.2 4.2M9.1 4.9 4.9 9.1"}
            stroke="var(--surface-2)" strokeWidth="1.5" fill="none"
          />
        </>
      ) : (
        <>
          <circle cx="7" cy="7" r="5.5" fill="none" strokeDasharray={type === "backlog" || type === "triage" ? "2 1.6" : undefined} />
          {type === "started" ? <path d="M7 3.5a3.5 3.5 0 0 1 0 7Z" fill="currentColor" stroke="none" /> : null}
        </>
      )}
    </svg>
  );
}
